import React, { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import {
  isAmountTarget,
  TARGET_TYPE_LABELS,
  type LodgementTargetDraft,
  type LodgementTargetType,
  type TargetPeriod,
  type TargetProgress,
} from '../../hooks/useLodgementTargets';
import { OwnerSelect, type OwnerOptionGroup } from './OwnerSelect';
import {
  Field, inputCls, money, attainmentChip, todayLocalIso,
} from './LodgementDisplay';

/**
 * Manage lodgement targets.
 *
 * WHY THIS EXISTS. The backend has supported four target types since the
 * feature shipped and the page has always drawn progress bars against them, but
 * nothing in the product could create one: targets were reachable only by
 * writing a Firestore document by hand. A number nobody can set is not a
 * feature, so this is the missing half.
 *
 * WHAT IT CAN AND CANNOT DO, and why the difference is not a shortcut:
 *
 *   CREATE   fully supported, all four types, count or dollars.
 *   RETIRE   `deleteLodgementTarget`, a soft delete (`isActive: false`).
 *   REPLACE  create the new target, then retire the old one.
 *
 * There is deliberately NO in-place edit. `upsertLodgementTarget` accepts an id
 * and would happily take one, but it validates the entire input including
 * `effectiveFrom`, and no callable on this feature ever returns a stored target
 * record: `getLodgementTargetProgress` yields the EVALUATED progress
 * (targetId, type, period, actual, target, percent, notApplicable) and nothing
 * else. `effectiveFrom`, `effectiveTo` and `label` are unreadable from the
 * client, and Firestore rules carry no entry for `lodgement_targets`, so a
 * direct read is denied too. An "edit" built on that would ask the admin to
 * retype an effective date they cannot see, and silently rewrite the window a
 * past period was judged against. Superseding is both honest and the model the
 * schema documents for itself.
 *
 * REPLACE ORDERS ITS WRITES DELIBERATELY. Create first, retire second. There is
 * no transaction across two callables, so one of them has to be able to fail
 * second: a failed retire leaves two active targets, which is visible on the
 * page and fixable from this list, whereas a failed create after a retire would
 * leave the person with no target at all and nothing on screen to say so.
 */

const TYPE_ORDER: LodgementTargetType[] = [
  'lodgement_count', 'lodgement_amount', 'settlement_count', 'settlement_amount',
];

const TYPE_HINTS: Record<LodgementTargetType, string> = {
  lodgement_count: 'deals lodged in the period, counted on the date lodged',
  lodgement_amount: 'dollars lodged in the period, on the date lodged',
  settlement_count: 'deals settled in the period, counted on the settlement date',
  settlement_amount: 'dollars settled in the period, on the settlement date',
};

/** Mirrors the server schema so the admin gets a clean message, not a rejection. */
const MAX_COUNT = 10_000;
const MAX_CENTS = 1_000_000_000_00;

interface Props {
  /** Every active company target, evaluated against the selected period. */
  progress: TargetProgress[];
  loading: boolean;
  error: string | null;
  /** Human label for the window the attainment column is measured over. */
  periodLabel: string;
  ownerGroups: OwnerOptionGroup[];
  ownerLabels: Map<string, string>;
  createTarget: (draft: LodgementTargetDraft) => Promise<string>;
  retireTarget: (id: string) => Promise<void>;
  /** Prefilled when opened from a gauge that had no target. */
  initial?: { ownerId?: string; type?: LodgementTargetType } | null;
  onClose: () => void;
}

interface Draft {
  ownerId: string;
  type: LodgementTargetType;
  period: TargetPeriod;
  /** Kept as typed text: a count or a DOLLAR amount, depending on `type`. */
  threshold: string;
  label: string;
  effectiveFrom: string;
  effectiveTo: string;
}

function emptyDraft(initial?: Props['initial']): Draft {
  return {
    ownerId: initial?.ownerId ?? '',
    type: initial?.type ?? 'lodgement_count',
    period: 'month',
    threshold: '',
    label: '',
    effectiveFrom: todayLocalIso(),
    effectiveTo: '',
  };
}

/** The threshold, in the unit the type demands. Never both, never a float. */
type ParsedThreshold =
  | { ok: true; thresholdCount?: number; thresholdCents?: number }
  | { ok: false; message: string };

export function parseThreshold(type: LodgementTargetType, raw: string): ParsedThreshold {
  const text = raw.trim().replace(/[$,\s]/g, '');
  if (!text) return { ok: false, message: 'Enter a target value.' };
  const n = Number(text);
  if (!Number.isFinite(n) || n <= 0) return { ok: false, message: 'Enter a positive number.' };

  if (!isAmountTarget(type)) {
    if (!Number.isInteger(n)) return { ok: false, message: 'A deal count must be a whole number.' };
    if (n > MAX_COUNT) return { ok: false, message: `The most a count target may be is ${MAX_COUNT.toLocaleString()}.` };
    return { ok: true, thresholdCount: n };
  }

  /**
   * Dollars in, INTEGER CENTS out, rounded explicitly.
   *
   * `5_000_000 * 100` is exact in binary floating point but `1234.56 * 100` is
   * not, and an unrounded product reaches the wire as 123455.99999999999, which
   * the server rejects for not being an integer. Rounding here is the repo's
   * money rule applied at the only boundary where dollars are allowed to exist.
   */
  const cents = Math.round(n * 100);
  if (cents <= 0) return { ok: false, message: 'Enter a positive dollar amount.' };
  if (cents > MAX_CENTS) return { ok: false, message: 'That is above the maximum target of $1 billion.' };
  return { ok: true, thresholdCents: cents };
}

export function LodgementTargetsModal({
  progress, loading, error, periodLabel, ownerGroups, ownerLabels,
  createTarget, retireTarget, initial, onClose,
}: Props): React.ReactElement {
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(initial));
  const [showForm, setShowForm] = useState<boolean>(Boolean(initial));
  /** The target this form supersedes, if any. */
  const [replacing, setReplacing] = useState<TargetProgress | null>(null);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [retiringId, setRetiringId] = useState<string | null>(null);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const amount = isAmountTarget(draft.type);

  const listed = useMemo(
    () =>
      [...progress].sort((a, b) => {
        const owner = (ownerLabels.get(a.ownerId) ?? a.ownerId).localeCompare(
          ownerLabels.get(b.ownerId) ?? b.ownerId, 'en-AU'
        );
        return owner !== 0 ? owner : TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type);
      }),
    [progress, ownerLabels]
  );

  const openCreate = (prefill?: { ownerId?: string; type?: LodgementTargetType }) => {
    setDraft(emptyDraft(prefill));
    setReplacing(null);
    setFormErr(null);
    setNotice(null);
    setShowForm(true);
  };

  const openReplace = (p: TargetProgress) => {
    setDraft({
      ...emptyDraft({ ownerId: p.ownerId, type: p.type }),
      period: p.period,
      // Seeded from the evaluated threshold, which IS readable, so the admin
      // adjusts a number rather than recalling one.
      threshold: isAmountTarget(p.type) ? String(Math.round(p.target / 100)) : String(p.target),
    });
    setReplacing(p);
    setFormErr(null);
    setNotice(null);
    setShowForm(true);
  };

  const submit = async () => {
    setFormErr(null);
    setNotice(null);

    const parsed = parseThreshold(draft.type, draft.threshold);
    if (!parsed.ok) {
      setFormErr(parsed.message);
      return;
    }
    if (draft.effectiveTo && draft.effectiveTo < draft.effectiveFrom) {
      setFormErr('The end date cannot be before the start date.');
      return;
    }

    setSaving(true);
    try {
      await createTarget({
        ...(draft.ownerId ? { ownerId: draft.ownerId } : {}),
        type: draft.type,
        period: draft.period,
        ...(parsed.thresholdCount != null ? { thresholdCount: parsed.thresholdCount } : {}),
        ...(parsed.thresholdCents != null ? { thresholdCents: parsed.thresholdCents } : {}),
        ...(draft.label.trim() ? { label: draft.label.trim() } : {}),
        effectiveFrom: draft.effectiveFrom,
        ...(draft.effectiveTo ? { effectiveTo: draft.effectiveTo } : {}),
      });

      if (replacing) {
        // Second write, on purpose (see the note at the top of this file). If it
        // fails, say exactly what state the tenant is in rather than reporting a
        // generic failure for a create that actually succeeded.
        try {
          await retireTarget(replacing.targetId);
          setNotice('Target replaced. The previous one has been retired.');
        } catch (e) {
          console.error('[LodgementTargetsModal] retire after replace failed', e);
          setFormErr(
            'The new target was created but the old one could not be retired, so both are '
            + 'active and progress will show two bars. Retire the old one from the list below.'
          );
          setReplacing(null);
          setShowForm(false);
          return;
        }
      } else {
        setNotice('Target created.');
      }

      setReplacing(null);
      setShowForm(false);
      setDraft(emptyDraft());
    } catch (e) {
      console.error('[LodgementTargetsModal] create failed', e);
      setFormErr(e instanceof Error ? e.message : 'Could not save the target.');
    } finally {
      setSaving(false);
    }
  };

  const retire = async (p: TargetProgress) => {
    setFormErr(null);
    setNotice(null);
    setRetiringId(p.targetId);
    try {
      await retireTarget(p.targetId);
      setNotice('Target retired.');
    } catch (e) {
      console.error('[LodgementTargetsModal] retire failed', e);
      setFormErr(e instanceof Error ? e.message : 'Could not retire the target.');
    } finally {
      setRetiringId(null);
    }
  };

  const fmtThreshold = (p: TargetProgress): string =>
    isAmountTarget(p.type) ? money(p.target) : String(p.target);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="max-h-[88vh] w-full max-w-3xl overflow-auto rounded-xl border border-gray-700 bg-gray-900">
        <div className="sticky top-0 flex items-start justify-between border-b border-gray-700 bg-gray-900 px-5 py-4">
          <div>
            <h3 className="text-lg font-bold text-white">Manage targets</h3>
            <p className="text-[11px] text-gray-400">
              Attainment is shown for {periodLabel}. A target applies to a period
              only when its own effective window overlaps it.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-gray-400 hover:bg-gray-800 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {error && (
            <div className="rounded-lg border border-red-700/40 bg-red-900/10 p-3 text-[12px] text-red-200">
              Could not load targets: {error}
            </div>
          )}
          {notice && (
            <div className="rounded-lg border border-emerald-700/40 bg-emerald-900/10 p-3 text-[12px] text-emerald-200">
              {notice}
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  {['Who', 'Target', 'Period', 'Value', `Attainment (${periodLabel})`, ''].map((h, i) => (
                    <th
                      key={h || `actions-${i}`}
                      scope="col"
                      className={`whitespace-nowrap border-b border-gray-700 px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 ${
                        i === 3 || i === 4 ? 'text-right' : 'text-left'
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-400">Loading…</td>
                  </tr>
                )}
                {!loading && listed.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-400">
                      No targets have been set for this company yet.
                    </td>
                  </tr>
                )}
                {!loading && listed.map((p) => (
                  <tr key={p.targetId} className="border-b border-gray-800/60 last:border-b-0">
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-white">
                      {ownerLabels.get(p.ownerId) ?? p.ownerId}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-300">
                      {TARGET_TYPE_LABELS[p.type]}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm capitalize text-gray-400">{p.period}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums text-white">
                      {fmtThreshold(p)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-sm">
                      {p.notApplicable ? (
                        <span className="text-[11px] text-gray-500" title="This target's effective window does not overlap the selected period">
                          outside this period
                        </span>
                      ) : (
                        <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${attainmentChip(p.percent)}`}>
                          {p.percent}%
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-sm">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openReplace(p)}
                          className="rounded-md border border-gray-700 px-2 py-1 text-[11px] text-gray-300 hover:bg-gray-800"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={() => { void retire(p); }}
                          disabled={retiringId === p.targetId}
                          className="rounded-md border border-red-700/50 px-2 py-1 text-[11px] text-red-300 hover:bg-red-900/20 disabled:cursor-not-allowed disabled:opacity-50"
                          title="Retiring removes this target from progress for every period, past ones included"
                        >
                          {retiringId === p.targetId ? 'Retiring…' : 'Retire'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!showForm && (
            <button
              type="button"
              onClick={() => openCreate()}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500"
            >
              <Plus className="h-4 w-4" />
              New target
            </button>
          )}

          {showForm && (
            <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-4">
              <h4 className="text-sm font-semibold text-white">
                {replacing
                  ? `Replace ${TARGET_TYPE_LABELS[replacing.type]} for ${ownerLabels.get(replacing.ownerId) ?? replacing.ownerId}`
                  : 'New target'}
              </h4>
              {replacing && (
                <p className="mt-1 text-[11px] text-amber-300">
                  This creates the new target and then retires the current one.
                  A retired target no longer counts towards any period, including
                  months already gone.
                </p>
              )}

              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Field
                  label="Whose target"
                  className="sm:col-span-2"
                  hint="leave as Myself to set your own"
                >
                  <OwnerSelect
                    value={draft.ownerId}
                    onChange={(ref) => set('ownerId', ref)}
                    groups={ownerGroups}
                    emptyLabel="Myself"
                    className={inputCls}
                  />
                </Field>

                <Field label="What is measured" required hint={TYPE_HINTS[draft.type]}>
                  <select
                    value={draft.type}
                    onChange={(e) => {
                      // Clear the value with the unit: a 12 typed as a deal
                      // count must never survive into a dollar field.
                      set('type', e.target.value as LodgementTargetType);
                      set('threshold', '');
                    }}
                    className={inputCls}
                  >
                    {TYPE_ORDER.map((t) => (
                      <option key={t} value={t}>{TARGET_TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Period" required hint="the window the target is judged over">
                  <select
                    value={draft.period}
                    onChange={(e) => set('period', e.target.value as TargetPeriod)}
                    className={inputCls}
                  >
                    <option value="month">Month</option>
                    <option value="quarter">Quarter</option>
                  </select>
                </Field>

                <Field
                  label={amount ? 'Target amount' : 'Target count'}
                  required
                  hint={amount ? 'dollars; stored as integer cents' : 'whole number of deals'}
                >
                  <div className="relative">
                    {amount && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
                        <span className="text-sm text-gray-400">$</span>
                      </div>
                    )}
                    <input
                      type="number"
                      min={1}
                      step={amount ? 1000 : 1}
                      value={draft.threshold}
                      onChange={(e) => set('threshold', e.target.value)}
                      className={amount ? `${inputCls} pl-6` : inputCls}
                      placeholder={amount ? '5000000' : '8'}
                    />
                  </div>
                </Field>

                <Field label="Label" hint="optional; how this target is described">
                  <input
                    value={draft.label}
                    onChange={(e) => set('label', e.target.value.slice(0, 120))}
                    className={inputCls}
                    placeholder="e.g. FY26 stretch"
                  />
                </Field>

                <Field
                  label="Effective from"
                  required
                  hint="periods before this date are not judged against it"
                >
                  <input
                    type="date"
                    value={draft.effectiveFrom}
                    onChange={(e) => set('effectiveFrom', e.target.value)}
                    className={inputCls}
                  />
                </Field>

                <Field label="Effective to" hint="optional; leave blank for open-ended">
                  <input
                    type="date"
                    value={draft.effectiveTo}
                    onChange={(e) => set('effectiveTo', e.target.value)}
                    className={inputCls}
                  />
                </Field>
              </div>

              {formErr && (
                <div className="mt-3 rounded-lg border border-red-700/40 bg-red-900/10 p-3 text-[12px] text-red-200">
                  {formErr}
                </div>
              )}

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setReplacing(null); setFormErr(null); }}
                  className="rounded-md border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => { void submit(); }}
                  disabled={saving || !draft.threshold.trim() || !draft.effectiveFrom}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? 'Saving…' : replacing ? 'Replace target' : 'Create target'}
                </button>
              </div>
            </div>
          )}

          <p className="text-[11px] text-gray-500">
            Targets are what someone is measured against, so only a super admin
            or an administrator may set them. Retiring keeps the record but stops
            it counting: it is removed from progress for every period, past ones
            included.
          </p>
        </div>
      </div>
    </div>
  );
}
